/* ==========================================================================
   A/B EXPERIMENT: "category-pages-redesign"
   Behaviour for the B collection layouts.

   Nothing in here runs for control: every entry point waits on
   window.KpodAB.onReady and returns immediately unless the resolved variant is
   the treatment. sections/kpod-ab-bootstrap.liquid owns that resolution.

   All listeners are delegated on `document`. That matters: facets.min.js handles
   filtering and sorting by replacing the whole [main-content] element, so any
   listener bound directly to a card would be discarded on the first filter click.
   Delegation means there is nothing to re-initialise.
   ========================================================================== */
(function () {
  'use strict';

  var AB = window.KpodAB;
  if (!AB || typeof AB.onReady !== 'function') return;

  AB.onReady(function (variant) {
    if (variant !== AB.TREATMENT) return;
    init();
  });

  /* ---------------------------------------------------------------- helpers */

  function track(event, props) {
    if (typeof posthog === 'undefined' || !posthog.capture) return;
    var payload = props || {};
    // The variant is also registered as a super-property in the bootstrap; these
    // are belt-and-braces so an event can never land without its split.
    payload.ab_variant = AB.variant;
    payload.page = AB.page;
    try { posthog.capture(event, payload); } catch (e) { /* never break the page for analytics */ }
  }

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  // Prefer the cart's own currency so the money shown matches whatever the
  // currency selector is set to. Fall back to the shop's money_format template.
  function formatMoney(cents, currency) {
    if (currency && typeof Intl !== 'undefined' && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: currency
        }).format(cents / 100);
      } catch (e) { /* unknown currency code -> template below */ }
    }
    var fmt = AB.moneyFormat || '\u00A3{{amount}}';
    return fmt
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amount(cents, 0, '.', ''))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount(cents, 2, '.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amount(cents, 0, ',', ''))
      .replace(/\{\{\s*amount\s*\}\}/g, amount(cents, 2, ',', '.'));
  }

  // Shopify's money_format placeholders differ only in their separators and
  // whether decimals are shown.
  function amount(cents, decimals, thousandsSep, decimalSep) {
    var parts = (cents / 100).toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    return parts.length > 1 ? parts[0] + decimalSep + parts[1] : parts[0];
  }

  /* ------------------------------------------------------------ pack cards */

  function onPackChange(input, silent) {
    var card = closest(input, '[data-kpod-ab-card]');
    if (!card) return;

    var hidden = card.querySelector('[data-kpod-ab-variant-input]');
    if (hidden) hidden.value = input.value;

    // Move the quantity bounds to the newly selected variant, then re-clamp.
    var qty = card.querySelector('.kpod-ab-qty__input');
    if (qty) {
      var min = input.dataset.qtyMin || '1';
      var step = input.dataset.qtyStep || '1';
      qty.min = min;
      qty.step = step;
      if (input.dataset.qtyMax) qty.max = input.dataset.qtyMax;
      else qty.removeAttribute('max');

      var value = parseInt(qty.value, 10);
      if (isNaN(value) || value < parseInt(min, 10)) value = parseInt(min, 10);
      if (qty.max && value > parseInt(qty.max, 10)) value = parseInt(qty.max, 10);
      qty.value = value;
      // Lets <hdt-quantity-wrapp> re-run its own bounds check and re-disable
      // the +/- buttons, rather than duplicating that logic here.
      qty.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (silent) return;

    track('variant_pack_selected', {
      product_handle: card.dataset.productHandle,
      product_id: card.dataset.productId,
      product_title: card.dataset.productTitle,
      variant_id: input.value,
      variant_title: input.dataset.packLabel,
      price: input.dataset.price ? parseInt(input.dataset.price, 10) / 100 : null
    });
  }

  /* ----------------------------------------------------------- add to cart */

  function onCardSubmit(form) {
    var card = closest(form, '[data-kpod-ab-card]');
    if (!card) return;

    var idInput = form.querySelector('[name="id"]');
    var qtyInput = form.querySelector('[name="quantity"]');
    var variantId = idInput ? idInput.value : null;
    var selected = card.querySelector('.kpod-ab-packs__input:checked');

    track('collection_add_to_cart', {
      product_handle: card.dataset.productHandle,
      product_id: card.dataset.productId,
      product_title: card.dataset.productTitle,
      variant_id: variantId,
      variant_title: selected ? selected.dataset.packLabel : null,
      quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1,
      price: selected && selected.dataset.price ? parseInt(selected.dataset.price, 10) / 100 : null
    });
  }

  /* ------------------------------------------------------------ cart state */

  // Watch the header cart count for any change, whatever caused it.
  //
  // Observing the count element itself is not enough: the theme re-renders whole
  // sections through the Section Rendering API, which REPLACES that node — and an
  // observer bound to a replaced node silently stops firing. So observe a stable
  // ancestor and re-query the count on every callback.
  function watchCartCount() {
    if (typeof MutationObserver === 'undefined') return;
    var probes = document.querySelectorAll('hdt-cart-count');
    if (!probes.length) return;

    // Observe every distinct section that holds a count element — themes commonly
    // render one for the desktop header and another for mobile, and the theme may
    // re-render either. De-duplicated so we never observe the same root twice.
    var roots = [];
    probes.forEach(function (p) {
      var root = p.closest('.shopify-section') || document.body;
      if (roots.indexOf(root) === -1) roots.push(root);
    });

    var seen = readCartCount();
    var observer = new MutationObserver(function () {
      var now = readCartCount();
      if (now === seen) return;
      AB.lastTrigger = 'cart-count ' + seen + ' -> ' + now;
      seen = now;
      refreshCart();
    });
    roots.forEach(function (root) {
      observer.observe(root, { childList: true, characterData: true, subtree: true });
    });
  }

  // Join every count element rather than reading only the first: whichever one the
  // theme updates, the joined value changes.
  function readCartCount() {
    var els = document.querySelectorAll('hdt-cart-count');
    if (!els.length) return null;
    return Array.prototype.map.call(els, function (e) { return e.textContent.trim(); }).join('|');
  }

  function onCartEvent(e) {
    // Diagnostics: which of the theme's cart events actually reached us, and
    // whether they carried a payload. Read with KpodAB.debug() in the console.
    AB.cartEvents.push((e && e.type) + (e && e.detail && e.detail.cart ? '+cart' : ''));
    if (AB.cartEvents.length > 20) AB.cartEvents.shift();
    AB.lastTrigger = 'event ' + (e && e.type);

    var cart = e && e.detail && e.detail.cart;
    if (cart && typeof cart.item_count === 'number') {
      applyCart(cart);
      return;
    }
    refreshCart();
  }

  // Poll /cart.js until the item count actually moves, then stop.
  //
  // Why this exists: the theme signals cart updates by dispatching 'cart:change',
  // and its OWN header count element is the proof it is not always dispatched — we
  // observed the badge stale at 2 with three items really in the cart. So the offer
  // bar cannot rely on the theme's events, nor on the badge those events update.
  //
  // Instead, whenever the shopper does something that changes the cart from a
  // control we own (our add-to-basket form, the drawer's remove link, the drawer's
  // quantity steppers) we re-read the cart ourselves for a few seconds. Bounded,
  // stops as soon as it sees a change, and costs nothing when nothing happens.
  var pollTimers = [];
  function pollCartForChange() {
    pollTimers.forEach(clearTimeout);
    pollTimers = [];
    var baseline = AB.lastItemCount;
    [350, 900, 1800, 3200].forEach(function (delay) {
      pollTimers.push(setTimeout(function () {
        if (AB.lastItemCount !== baseline) return; // already picked the change up
        AB.lastTrigger = 'poll @' + delay + 'ms';
        doRefreshCart();
      }, delay));
    });
  }

  var refreshTimer = null;
  function refreshCart() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(doRefreshCart, 60);
  }

  function doRefreshCart() {
    if (!document.querySelector('[data-kpod-ab-basket]')) return;

    // cache: 'no-store' matters — a cached /cart.js response would show the cart as
    // it was before the add, which reads as "the bar only updates on refresh".
    fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store',
      credentials: 'same-origin'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart) applyCart(cart);
      })
      .catch(function () { /* leave the server-rendered values in place */ });
  }

  function applyCart(cart) {
    AB.applyCount = (AB.applyCount || 0) + 1;
    var basket = document.querySelector('[data-kpod-ab-basket]');
    if (basket) updateBasketBar(basket, cart);
  }

  function updateBasketBar(basket, cart) {
    var threshold = parseInt(basket.dataset.threshold, 10) || 0;
    var count = cart.item_count || 0;
    AB.lastItemCount = count;

    basket.classList.toggle('kpod-ab-basket--visible', count > 0);
    if (count === 0) return;

    var line = basket.querySelector('[data-kpod-ab-basket-line]');
    if (line) {
      line.textContent = count + ' ' + (count === 1 ? 'item' : 'items') +
        ' · ' + formatMoney(cart.total_price, cart.currency);
    }

    var shippable = (cart.items || []).reduce(function (sum, item) {
      return item.requires_shipping ? sum + item.final_line_price : sum;
    }, 0);
    var remaining = Math.max(0, threshold - shippable);

    var ship = basket.querySelector('[data-kpod-ab-basket-ship]');
    if (ship) {
      var free = remaining === 0;
      ship.classList.toggle('kpod-ab-basket__ship--free', free);
      ship.textContent = free
        ? ship.dataset.successText || 'FREE next-day delivery unlocked'
        : formatMoney(remaining, cart.currency) + ' to ' + (ship.dataset.progressText || 'FREE next-day delivery');
    }
  }

  /* -------------------------------------------------------------------- FAQ */

  function toggleFaq(button) {
    var expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    var panel = document.getElementById(button.getAttribute('aria-controls'));
    if (panel) panel.hidden = expanded;
  }

  /* ------------------------------------------------------------------ init */

  function init() {
    AB.initialised = true;
    AB.cartEvents = [];
    AB.debug = function () {
      return {
        initialised: AB.initialised === true,
        variant: AB.variant,
        cartEventsSeen: AB.cartEvents,
        lastItemCount: AB.lastItemCount,
        cartCountInHeader: readCartCount(),
        lastTrigger: AB.lastTrigger || 'none since load',
        applyCount: AB.applyCount || 0
      };
    };

    document.addEventListener('change', function (e) {
      var input = closest(e.target, '.kpod-ab-packs__input');
      if (input) onPackChange(input);
    });

    // Bubble phase, so the theme's own ProductForm submit handler has already run.
    // It calls preventDefault but not stopPropagation, so this still fires.
    document.addEventListener('submit', function (e) {
      var form = closest(e.target, '.kpod-ab-card__form, .kpod-ab-mcard__form');
      if (!form) return;
      onCardSubmit(form);
      pollCartForChange();
    });

    // Cart edits made in the drawer: the remove link and the quantity steppers.
    // Delegated, so it keeps working when the drawer is re-rendered.
    document.addEventListener('click', function (e) {
      if (closest(e.target, '.hdt-mini-cart__remove, [data-quantity-wrapper] button, .hdt-quantity-cart-item button')) {
        pollCartForChange();
      }
    });
    document.addEventListener('change', function (e) {
      if (closest(e.target, '[will-change-cart]')) pollCartForChange();
    });

    document.addEventListener('click', function (e) {
      var faq = closest(e.target, '[data-kpod-ab-faq-toggle]');
      if (faq) { toggleFaq(faq); return; }

      var upgrade = closest(e.target, '[data-kpod-ab-bundle-upgrade]');
      if (upgrade) {
        track('bundle_upgrade_clicked', { source: upgrade.dataset.source || 'unknown' });
      }
    });

    // Cart events. The theme uses different names depending on what is installed:
    // with a cart drawer present its own components listen for 'cart:drawer:change'
    // rather than 'variant:add' (see assets/custom.js), and quantity edits inside
    // the drawer go out as 'cart:refresh'. Listen for all of them.
    //
    // Where an event carries detail.cart we use it directly — instant, and immune to
    // a cached /cart.js. Otherwise we re-fetch.
    ['cart:change', 'cart:drawer:change', 'cart:drawer:render', 'cart:refresh', 'variant:add']
      .forEach(function (name) { document.addEventListener(name, onCartEvent); });

    // Backstop, independent of any event name: the header cart count is updated by
    // the theme however the cart changed — drawer, quantity stepper, an app, a
    // future refactor. Watching it means this cannot silently stop working if the
    // theme's private event names change.
    watchCartCount();

    // The server renders the hidden `id` from selected_or_first_available_variant
    // and the checked radio from the first available pack. Those agree in
    // practice, but a mismatch would silently add the wrong variant, so reconcile
    // from the radio — which is what the shopper can actually see — on load and
    // after every filter/sort re-render.
    syncCards();
    document.addEventListener('theme:loading:done', syncCards);

    startDebugPanel();

    refreshCart();
  }

  // On-screen diagnostics, opt-in via ?kpod_ab_debug=1 — no devtools needed.
  // Shows live what is driving the offer bar. Nothing renders without the flag,
  // so this is inert for shoppers. Safe to delete once the test is settled.
  function startDebugPanel() {
    var on = false;
    try { on = new URLSearchParams(window.location.search).get('kpod_ab_debug') === '1'; } catch (e) { return; }
    if (!on) return;

    var box = document.createElement('div');
    box.setAttribute('style', [
      'position:fixed', 'left:8px', 'bottom:8px', 'z-index:99999',
      'background:rgba(15,15,15,.94)', 'color:#fff', 'padding:10px 12px',
      'border-radius:8px', 'font:11px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace',
      'max-width:340px', 'white-space:pre-wrap', 'pointer-events:none'
    ].join(';'));
    document.body.appendChild(box);

    setInterval(function () {
      var d = AB.debug();
      box.textContent =
        'KPod A/B debug\n' +
        'init:        ' + d.initialised + '\n' +
        'variant:     ' + d.variant + '\n' +
        'header count:' + d.cartCountInHeader + '\n' +
        'items:       ' + d.lastItemCount + '\n' +
        'updates:     ' + d.applyCount + '\n' +
        'last trigger:' + d.lastTrigger + '\n' +
        'events:      ' + (d.cartEventsSeen.slice(-4).join(', ') || 'none');
    }, 400);
  }

  function syncCards() {
    document.querySelectorAll('.kpod-ab-packs__input:checked').forEach(function (input) {
      onPackChange(input, true);
    });
  }
})();
